import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import { z } from "zod";
import { apiGet } from "../../lib/api";
import { type CartePublique, cartePubliqueSchema, type MotifSignalement } from "../contract";
import { envoyer } from "../diffusion-api";

/**
 * Le client d'API de la page publique d'une carte (`/api/taverne/cartes/{ref}`), sans session. La lecture ne
 * reessaie pas : une carte inconnue (404) se dit tout de suite. Le signalement est une ecriture de la diffusion
 * (`diffusion-api.ts` : delai, `ApiError` au code de l'API, jamais le message du serveur).
 */

const RACINE = "/api/taverne";

export const cleCarte = (ref: string) => ["cartes", "publique", ref] as const;

export function useCartePublique(ref: string): UseQueryResult<CartePublique, Error> {
  return useQuery({
    queryKey: cleCarte(ref),
    retry: false,
    queryFn: () => apiGet(`${RACINE}/cartes/${encodeURIComponent(ref)}`, cartePubliqueSchema),
  });
}

/** Une reponse sans corps (204) se lit comme `null`. */
const SANS_CORPS = z.null();

export function useSignaler(ref: string): UseMutationResult<null, Error, MotifSignalement> {
  return useMutation({
    mutationFn: (motif: MotifSignalement) =>
      envoyer(
        "POST",
        `${RACINE}/cartes/${encodeURIComponent(ref)}/signalement`,
        { motif },
        SANS_CORPS,
      ),
  });
}

/** L'adresse de partage d'une carte : la page d'apercu que Discord sait lire (`partage.ts`), qui renvoie vers la carte. */
export function adresseDePartage(origine: string, ref: string): string {
  return `${origine}${RACINE}/partage/c/${encodeURIComponent(ref)}`;
}
